import React, { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { dataImportService } from '@/services/data-import.service';
import { ImportStatus, ImportResult } from '@/types/data-import.types';
import FileUploadZone from './FileUploadZone';
import ImportProgress from './ImportProgress';
import ImportResults from './ImportResults';
import toast from 'react-hot-toast';

interface ImportLoansModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ImportLoansModal: React.FC<ImportLoansModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus>({
    stage: 'idle',
    progress: 0,
  });
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validate file
      const validation = await dataImportService.validateFile(file);

      if (!validation.valid) {
        toast.error('Invalid File: ' + validation.errors?.join(', '));
        return;
      }

      setSelectedFile(file);
    },
    [toast]
  );

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    try {
      setImportStatus({ stage: 'uploading', progress: 0 });

      const result = await dataImportService.importLoans(selectedFile, {
        onProgress: (progress) => {
          setImportStatus((prev) => ({ ...prev, progress }));
        },
      });

      setImportStatus({ stage: 'complete', progress: 100 });
      setImportResult(result);

      if (result.success && result.imported_count > 0) {
        toast.success(`Successfully imported ${result.imported_count} loans`);

        if (onSuccess) {
          onSuccess();
        }
      } else if (result.failed_count > 0) {
        toast.error(
          `Imported ${result.imported_count} loans, ${result.failed_count} failed`
        );
      }
    } catch (error) {
      setImportStatus({
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Import failed',
      });

      toast.error(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  }, [selectedFile, toast, onSuccess]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setImportStatus({ stage: 'idle', progress: 0 });
    setImportResult(null);
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setImportStatus({ stage: 'idle', progress: 0 });
    setImportResult(null);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      data-test-id="import-loans-modal-backdrop"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl"
        data-test-id="import-loans-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-6 border-b"
          data-test-id="import-loans-modal-header"
        >
          <h2
            className="text-2xl font-semibold text-gray-900"
            data-test-id="import-loans-modal-title"
          >
            Import Loans
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
            data-test-id="import-loans-modal-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]"
          data-test-id="import-loans-modal-content"
        >
          {importStatus.stage === 'idle' && !importResult && (
            <>
              <FileUploadZone
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                acceptedTypes={dataImportService.getAcceptedFileTypes()}
              />

              {selectedFile && (
                <div
                  className="mt-6 flex justify-end space-x-3"
                  data-test-id="import-loans-actions"
                >
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    data-test-id="import-loans-reset-button"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleImport}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
                    data-test-id="import-loans-import-button"
                  >
                    Import Loans
                  </button>
                </div>
              )}
            </>
          )}

          {(importStatus.stage === 'uploading' ||
            importStatus.stage === 'processing') && (
            <ImportProgress
              stage={importStatus.stage}
              progress={importStatus.progress}
              message={importStatus.message}
            />
          )}

          {importResult && importStatus.stage === 'complete' && (
            <ImportResults
              result={importResult}
              onClose={handleClose}
              onImportMore={handleReset}
            />
          )}

          {importStatus.stage === 'error' && (
            <div className="text-center py-8" data-test-id="import-error-state">
              <div className="text-red-600 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Import Failed
              </h3>
              <p className="text-gray-600 mb-6">{importStatus.message}</p>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
                data-test-id="import-error-retry-button"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center"
          data-test-id="import-loans-modal-footer"
        >
          <p
            className="text-sm text-gray-600"
            data-test-id="import-loans-footer-text"
          >
            Supported formats: Excel (.xlsx, .xls) and CSV files up to 10MB
          </p>
          {importStatus.stage === 'idle' && !importResult && (
            <button
              onClick={handleClose}
              className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              data-test-id="import-loans-cancel-button"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportLoansModal;
